import type { ComponentChildren, JSX } from "preact"
import { useEffect, useRef, useState } from "preact/hooks"

import type { WidgetPayload } from "@workspace/contracts/embed"
// The dedicated entry, like `embed/reveal`: the index would drag zod in.
import { MESSENGER as M } from "@workspace/contracts/embed/presets"

import { partClass, type Look } from "../appearance"
import type { Conversation } from "../chat/use-conversation"
import { Drawer } from "../drawer"
import {
  ChevronDown,
  ExternalLink,
  Info,
  MessageCircleX,
  MessengerLogo,
  Minus,
  Phone,
  Send,
  Video,
  X,
} from "../icons"
import {
  type ChatConfig,
  useComposerHint,
  useSpotlights,
  useStickToBottom,
  welcomeOf,
} from "./chat-shared"

/**
 * The chat bubble in Messenger's chrome (`preset: "facebook-messenger"`),
 * the preview's messenger branch (`widget-preview.tsx`) carried over
 * number for number through `MESSENGER`: the avatar launcher with its
 * badge, the 372 x 520 panel with a plain header, grey agent bubbles with
 * the avatar on the first of a run, gradient visitor bubbles, the "Aa"
 * composer with a blue paper plane. The accent, alpha and radius are
 * inert; placement, the content and the customer's css still apply, on
 * the same part names as the default bubble. The call and info buttons
 * are the chrome, disabled - not features. The stop control is: once a
 * conversation exists it sits before Minimize, and the agent's "Stop the
 * conversation?" card (`chat.tsx` has the same, in the accent) answers in
 * the preset's own colours - blue outline, gradient fill.
 */

interface Props {
  payload: WidgetPayload
  config: ChatConfig
  look: Look
  chat: Conversation
  mobile: boolean
  focus: number
  open: boolean
  setOpen: (open: boolean) => void
  pinned: boolean
  onCta: () => void
}

/** The preset's palette for the host's light or dark. */
function paletteOf(hostDark: boolean) {
  const mode = hostDark ? "dark" : "light"
  return {
    surface: M.surface[mode],
    bubble: M.bubble[mode],
    muted: M.muted[mode],
    border: M.border[mode],
  }
}
type Palette = ReturnType<typeof paletteOf>

const round = (
  size: number,
  extra: JSX.CSSProperties = {}
): JSX.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  ...extra,
})

/** The agent's face: their picture, else their initial - on grey, or on `fallback`. */
function Avatar({
  payload,
  size,
  font,
  palette,
  fallback,
}: {
  payload: WidgetPayload
  size: number
  font: number
  palette: Palette
  fallback?: JSX.CSSProperties
}) {
  const name = payload.agent?.name ?? null
  const url = payload.agent?.avatarUrl ?? null
  if (url) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        style={round(size, { objectFit: "cover" })}
      />
    )
  }
  return (
    <span
      style={round(size, {
        background: palette.bubble,
        color: palette.muted,
        fontSize: font,
        fontWeight: 600,
        ...fallback,
      })}
    >
      {name ? name.trim().charAt(0).toUpperCase() : "A"}
    </span>
  )
}

/** One bubble; an agent's first of a run carries the avatar beside it. */
function Bubble({
  text,
  mine,
  runStart,
  look,
  config,
  payload,
  palette,
  children,
}: {
  text?: string
  mine: boolean
  runStart: boolean
  look: Look
  config: ChatConfig
  payload: WidgetPayload
  palette: Palette
  children?: ComponentChildren
}) {
  return (
    <div
      class="uq-rise"
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 6,
        justifyContent: mine ? "flex-end" : "flex-start",
        marginTop: runStart ? 6 : 2,
      }}
    >
      {!mine &&
        (runStart ? (
          <Avatar
            payload={payload}
            size={M.avatarSize}
            font={12}
            palette={palette}
          />
        ) : (
          <span style={{ width: M.avatarSize, flexShrink: 0 }} />
        ))}
      <div
        class={partClass(config, mine ? "userMessage" : "botMessage")}
        style={{
          maxWidth: M.bubbleMaxWidth,
          padding: children ? "8px 12px" : M.bubblePadding,
          borderRadius: M.bubbleRadius,
          background: mine ? M.gradient : palette.bubble,
          color: mine ? "#fff" : `rgb(${look.ink})`,
          fontSize: M.bubbleFont,
          lineHeight: 1.375,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {children ?? text}
      </div>
    </div>
  )
}

/** The stop card's body, inside an agent bubble: the preset's blue and gradient. */
function StopCard({
  chat,
  stopping,
}: {
  chat: Conversation
  stopping: NonNullable<Conversation["stopping"]>
}) {
  const button = {
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
    opacity: stopping.busy ? 0.6 : 1,
  }
  return (
    <div
      class="uq-stop"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <span style={{ fontWeight: 600 }}>Stop the conversation?</span>
      <span>Do you want to stop this conversation?</span>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => chat.answerStop(false)}
          disabled={stopping.busy}
          style={{ ...button, border: `1px solid ${M.blue}`, color: M.blue }}
        >
          Keep chatting
        </button>
        <button
          type="button"
          onClick={() => chat.answerStop(true)}
          disabled={stopping.busy}
          style={{ ...button, background: M.gradient, color: "#fff" }}
        >
          {stopping.busy ? "Stopping…" : "Stop conversation"}
        </button>
      </div>
      {stopping.error && (
        <span class="uq-fade" style={{ opacity: 0.7 }}>
          {stopping.error}
        </span>
      )}
    </div>
  )
}

/** The panel's chrome, header to composer; `fill` is the phone's sheet. */
function Panel({
  payload,
  config,
  look,
  chat,
  focus,
  onCta,
  mobile,
  fill = false,
  onMinimize,
  onClose,
}: {
  payload: WidgetPayload
  config: ChatConfig
  look: Look
  chat: Conversation
  focus: number
  onCta: () => void
  mobile: boolean
  fill?: boolean
  onMinimize?: () => void
  onClose?: () => void
}) {
  const palette = paletteOf(look.hostDark)
  const { surface, bubble, muted, border } = palette
  const hint = useComposerHint(config, M.composerPlaceholder)
  const name = payload.agent?.name ?? null
  const live = Boolean(payload.agent?.live)
  const column = useStickToBottom([
    chat.turns,
    chat.busy,
    chat.error,
    chat.stopping,
  ])
  const [value, setValue] = useState("")
  const input = useRef<HTMLInputElement>(null)
  const spotlit = useSpotlights(input, config)
  useEffect(() => {
    if (focus > 0) input.current?.focus()
  }, [focus])
  // Nothing sends while the stop card waits for its answer.
  const canSend = live && !chat.stopping
  const submit = (event: Event) => {
    event.preventDefault()
    if (!canSend) return
    chat.send(value)
    setValue("")
  }
  const cta = config.cta?.href?.trim() ? config.cta : null

  // The turns as one flat list so an agent's run knows its first bubble.
  const bubbles: { key: string; text: string; mine: boolean }[] = [
    { key: "welcome", text: welcomeOf(config), mine: false },
  ]
  for (const turn of chat.turns) {
    if (turn.role === "user") {
      bubbles.push({ key: turn.id, text: turn.parts[0] ?? "", mine: true })
    } else {
      turn.parts.slice(0, turn.shown).forEach((part, index) => {
        bubbles.push({ key: `${turn.id}:${index}`, text: part, mine: false })
      })
    }
  }
  const last = bubbles[bubbles.length - 1]!

  const headerButton = (
    icon: ComponentChildren,
    label: string,
    color: string,
    onClick?: () => void,
    part?: string
  ) => (
    <button
      type="button"
      class={part ? partClass(config, part) : undefined}
      onClick={onClick}
      disabled={!onClick}
      aria-label={label}
      style={round(mobile ? 36 : M.headerButton, {
        color,
        opacity: onClick ? 1 : 0.6,
      })}
    >
      {icon}
    </button>
  )
  const icon = M.headerIcon

  return (
    <div
      class={partClass(config, "panel")}
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: fill ? 0 : M.panelRadius,
        overflow: "hidden",
        border: fill ? undefined : `1px solid ${border}`,
        boxShadow: fill ? undefined : M.panelShadow,
        background: surface,
        color: `rgb(${look.ink})`,
        ...(fill
          ? { flex: 1, minHeight: 0 }
          : {
              height: M.panelHeight,
              maxHeight: `calc(100vh - ${look.offsetY + M.launcherSize + M.panelGap + 12}px)`,
            }),
      }}
    >
      <div
        class={partClass(config, "header")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Avatar
          payload={payload}
          size={M.headerAvatar}
          font={14}
          palette={palette}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <span
            style={{
              fontSize: M.nameFont,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name ? name.split("—")[0]!.trim() : "Chat"}
          </span>
          <span style={{ color: muted, opacity: 0.7, display: "flex" }}>
            <ChevronDown size={16} />
          </span>
        </span>
        {headerButton(<Phone size={icon} />, "Call (unavailable)", M.blue)}
        {headerButton(
          <Video size={icon} />,
          "Video call (unavailable)",
          M.blue
        )}
        {headerButton(<Info size={icon} />, "Details (unavailable)", muted)}
        {chat.sessionId &&
          headerButton(
            <MessageCircleX size={icon} />,
            "Stop conversation",
            muted,
            chat.busy || chat.stopping ? undefined : chat.askToStop,
            "stopButton"
          )}
        {onMinimize &&
          headerButton(
            <Minus size={icon} />,
            "Minimize chat",
            muted,
            onMinimize
          )}
        {onClose &&
          headerButton(
            <X size={icon} />,
            "Close chat",
            muted,
            onClose,
            "close"
          )}
      </div>
      <div
        ref={column}
        style={{
          flex: 1,
          minHeight: 0,
          padding: "6px 12px 12px",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {bubbles.map((b, index) => (
          <Bubble
            key={b.key}
            text={b.text}
            mine={b.mine}
            runStart={index === 0 || bubbles[index - 1]!.mine !== b.mine}
            look={look}
            config={config}
            payload={payload}
            palette={palette}
          />
        ))}
        {chat.stopping && (
          <Bubble
            mine={false}
            runStart={last.mine}
            look={look}
            config={config}
            payload={payload}
            palette={palette}
          >
            <StopCard chat={chat} stopping={chat.stopping} />
          </Bubble>
        )}
        {chat.busy && (
          <Bubble
            mine={false}
            runStart={last.mine}
            look={look}
            config={config}
            payload={payload}
            palette={palette}
          >
            <span class="uq-dots" aria-label="Typing" style={{ gap: 4 }}>
              <span style={{ width: 6, height: 6 }} />
              <span style={{ width: 6, height: 6 }} />
              <span style={{ width: 6, height: 6 }} />
            </span>
          </Bubble>
        )}
        {chat.error && (
          <div
            class="uq-error uq-fade"
            style={{
              display: "flex",
              gap: 8,
              marginTop: 6,
              paddingLeft: M.avatarSize + 6,
              fontSize: 12,
              color: muted,
            }}
          >
            <span>{chat.error}</span>
            <button
              type="button"
              onClick={chat.retry}
              style={{
                color: M.blue,
                fontWeight: 500,
                textDecoration: "underline",
              }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
      {cta && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px 12px",
          }}
        >
          <a
            class="uq-cta"
            href={cta.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCta}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 999,
              background: M.gradient,
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              boxShadow: M.ctaShadow,
            }}
          >
            {cta.label || "Learn more"}
            <ExternalLink size={16} />
          </a>
        </div>
      )}
      <form
        class={partClass(config, "composer")}
        onSubmit={submit}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: 8,
          borderTop: `1px solid ${border}`,
        }}
      >
        <input
          ref={input}
          type="text"
          value={value}
          onInput={(event) =>
            setValue((event.target as HTMLInputElement).value)
          }
          placeholder={spotlit ? undefined : hint}
          disabled={!canSend}
          maxLength={4000}
          aria-label="Message"
          autoComplete="off"
          style={{
            flex: 1,
            minHeight: M.composerHeight,
            padding: "0 12px",
            borderRadius: M.composerRadius,
            background: bubble,
            color: `rgb(${look.ink})`,
            // 16 on a phone so iOS does not zoom the page into the field.
            fontSize: mobile ? 16 : 14,
            "--uq-placeholder": muted,
          }}
        />
        {chat.busy ? (
          <button
            type="button"
            class={partClass(config, "sendButton")}
            onClick={chat.stop}
            aria-label="Stop"
            style={round(M.sendSize, { color: M.blue })}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: "currentColor",
                borderRadius: 2,
              }}
            />
          </button>
        ) : (
          <button
            type="submit"
            class={partClass(config, "sendButton")}
            disabled={!canSend || !value.trim()}
            aria-label="Send"
            style={round(M.sendSize, {
              color: M.blue,
              opacity: canSend && value.trim() ? 1 : 0.5,
            })}
          >
            <Send size={M.sendIcon} />
          </button>
        )}
      </form>
    </div>
  )
}

export function Messenger({
  payload,
  config,
  look,
  chat,
  mobile,
  focus,
  open,
  setOpen,
  pinned,
  onCta,
}: Props) {
  const palette = paletteOf(look.hostDark)
  const { side, offsetY } = look
  const close = pinned ? undefined : () => setOpen(false)

  // On a phone the open panel is a sheet (`drawer.tsx`) in the preset's
  // own surface colour and corners; the launcher waits under it.
  if (open && mobile) {
    const sheetLook = {
      ...look,
      surfaceBg: palette.surface,
      rad: M.panelRadius,
    }
    return (
      <Drawer look={sheetLook} pinned={pinned} onClose={() => setOpen(false)}>
        <Panel
          payload={payload}
          config={config}
          look={look}
          chat={chat}
          focus={focus}
          onCta={onCta}
          mobile
          fill
          onClose={close}
        />
      </Drawer>
    )
  }

  return (
    <>
      {open && (
        <div
          class="uq-fixed uq-rise"
          style={{
            bottom: offsetY + M.launcherSize + M.panelGap,
            width: M.panelWidth,
            ...side,
          }}
        >
          <Panel
            payload={payload}
            config={config}
            look={look}
            chat={chat}
            focus={focus}
            onCta={onCta}
            mobile={false}
            onMinimize={close}
            onClose={close}
          />
        </div>
      )}
      <button
        type="button"
        class={partClass(config, "launcher", "uq-fixed")}
        aria-label={open ? "Close chat" : "Open chat"}
        aria-expanded={open}
        onClick={() => {
          if (open && pinned) return
          setOpen(!open)
        }}
        style={{
          bottom: offsetY,
          ...round(M.launcherSize, { boxShadow: M.launcherShadow }),
          ...side,
        }}
      >
        <Avatar
          payload={payload}
          size={M.launcherSize}
          font={16}
          palette={palette}
          fallback={{ background: M.gradient, color: "#fff" }}
        />
        {open ? (
          // Open: the avatar dims under a chevron - "put it away".
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "rgba(0,0,0,.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <ChevronDown size={28} />
          </span>
        ) : (
          <span
            style={{
              position: "absolute",
              right: -4,
              bottom: -4,
              ...round(M.badgeSize, {
                background: M.accent,
                color: "#fff",
                boxShadow: `0 0 0 2px ${palette.surface}`,
              }),
            }}
          >
            <MessengerLogo size={12} d={M.logo} />
          </span>
        )}
      </button>
    </>
  )
}
